import Vue from "vue";
import { api } from "@/services/Api";
import Popup from "@/entity/Popup";

export interface ResponseEntry {
  uuid: string;
  value: any;
  currentValue: any;
  synced: boolean;
  questionId: number;
  questionUuid: string;
  rgUuid: string;
  comment?: string;
  responseGroups?: any[];
}

export interface FormStoreState {
  // Form structure (synced via POST /api/sync)
  surveys: { [slug: string]: any };
  steps: { [id: string]: any };
  sections: { [id: string]: any };
  questions: { [id: string]: any };
  questionsFiltered: any;
  choices: { [id: string]: any };
  tags: { [id: string]: any };
  suggestions: { [id: string]: any };
  conditionGroups: { [id: string]: any };
  conditions: { [id: string]: any };
  mappings: { [id: string]: any };

  // Live data
  responseGroups: { [uuid: string]: any };
  responses: { [rgUuid: string]: { [questionId: string]: ResponseEntry } };
  prospects: { [uuid: string]: any };

  // Branch data
  packCategories: any[];

  // Context
  currentResponseGroupUuid: string | null;
  currentSurveySlug: string | null;
  currentBranchSlug: string | null;

  // Sync state
  lastSyncAt: string | null;
  pendingSync: { [key: string]: { rgUuid: string; questionId: string } };
  syncTimer: any;
  isSyncing: boolean;
}

const DEBOUNCE_MS = 200;
const STORAGE_KEY = "formStore";

// Clés persistées dans localStorage (structure + lastSyncAt)
const PERSISTED_KEYS: (keyof FormStoreState)[] = [
  "surveys",
  "steps",
  "sections",
  "questions",
  "choices",
  "tags",
  "suggestions",
  "conditionGroups",
  "conditions",
  "mappings",
  "lastSyncAt",
];

function loadPersistedState(): Partial<FormStoreState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {
    console.warn("formStore: failed to load persisted state", e);
  }
  return {};
}

function persistState(state: FormStoreState) {
  try {
    const toPersist: any = {};
    PERSISTED_KEYS.forEach((key) => {
      toPersist[key] = state[key];
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toPersist));
  } catch (e) {
    console.warn("formStore: failed to persist state", e);
  }
}

const getDefaultState = (): FormStoreState => {
  const persisted = loadPersistedState();
  return {
    surveys: persisted.surveys || {},
    steps: persisted.steps || {},
    sections: persisted.sections || {},
    questions: persisted.questions || {},
    questionsFiltered: {},
    choices: persisted.choices || {},
    tags: persisted.tags || {},
    suggestions: persisted.suggestions || {},
    conditionGroups: persisted.conditionGroups || {},
    conditions: persisted.conditions || {},
    mappings: persisted.mappings || {},
    responseGroups: {},
    responses: {},
    prospects: {},
    packCategories: [],
    currentResponseGroupUuid: null,
    currentSurveySlug: null,
    currentBranchSlug: null,
    lastSyncAt: persisted.lastSyncAt || null,
    pendingSync: {},
    syncTimer: null,
    isSyncing: false,
  };
};

const state: FormStoreState = getDefaultState();

const mutations = {
  SET_PACK_CATEGORIES(state: FormStoreState, payload: any[]) {
    state.packCategories = payload;
  },

  // ─── Generic data operations ───

  ADD_DATA(
    state: FormStoreState,
    payload: { property: string; key: string; value: any }
  ) {
    if (!state[payload.property as keyof FormStoreState]) return;
    Vue.set(
      state[payload.property as keyof FormStoreState] as any,
      payload.key,
      payload.value
    );
  },

  SET_DATA(
    state: FormStoreState,
    payload: { property: string; value: any }
  ) {
    const prop = payload.property as keyof FormStoreState;
    if (state[prop] === undefined) return;
    if (typeof payload.value === "object" && payload.value !== null) {
      Object.keys(payload.value).forEach((key) => {
        Vue.set(state[prop] as any, key, payload.value[key]);
      });
    }
  },

  REMOVE_DATA(
    state: FormStoreState,
    payload: { property: string; key: string }
  ) {
    const prop = state[payload.property as keyof FormStoreState] as any;
    if (prop && prop[payload.key] !== undefined) {
      Vue.delete(prop, payload.key);
    }
  },

  // ─── Context ───

  SET_CURRENT_CONTEXT(
    state: FormStoreState,
    payload: { rgUuid?: string; surveySlug?: string; branchSlug?: string }
  ) {
    if (payload.rgUuid !== undefined)
      state.currentResponseGroupUuid = payload.rgUuid;
    if (payload.surveySlug !== undefined)
      state.currentSurveySlug = payload.surveySlug;
    if (payload.branchSlug !== undefined)
      state.currentBranchSlug = payload.branchSlug;
  },

  // ─── Survey flattening (from nested API response) ───

  STORE_SURVEY_FLAT(state: FormStoreState, survey: any) {
    if (!survey || !survey.slug) return;

    const stepIds: number[] = [];

    if (survey.steps && Array.isArray(survey.steps)) {
      survey.steps.forEach((step: any) => {
        const stepId = step.id;
        stepIds.push(stepId);

        const sectionIds: number[] = [];

        if (step.sections && Array.isArray(step.sections)) {
          step.sections.forEach((section: any) => {
            const sectionId = section.id;
            sectionIds.push(sectionId);

            const questionIds: number[] = [];

            if (section.questions && Array.isArray(section.questions)) {
              section.questions.forEach((question: any) => {
                const questionId = question.id;
                questionIds.push(questionId);

                const questionBase = { ...question };

                if (question.choices && Array.isArray(question.choices)) {
                  const choiceIds: number[] = [];
                  question.choices.forEach((choice: any) => {
                    choiceIds.push(choice.id);
                    Vue.set(state.choices, String(choice.id), {
                      ...choice,
                      question: questionId,
                    });
                  });
                  questionBase.choices = choiceIds;
                }

                if (
                  question.conditionGroups &&
                  Array.isArray(question.conditionGroups)
                ) {
                  const cgIds: number[] = [];
                  question.conditionGroups.forEach((cg: any) => {
                    cgIds.push(cg.id);
                    Vue.set(state.conditionGroups, String(cg.id), {
                      ...cg,
                      question: questionId,
                    });
                    if (cg.conditions && Array.isArray(cg.conditions)) {
                      cg.conditions.forEach((c: any) => {
                        Vue.set(state.conditions, String(c.id), {
                          ...c,
                          group: cg.id,
                        });
                      });
                    }
                  });
                  questionBase.conditionGroups = cgIds;
                }

                if (
                  question.suggestions &&
                  Array.isArray(question.suggestions)
                ) {
                  const sIds: number[] = [];
                  question.suggestions.forEach((s: any) => {
                    sIds.push(s.id);
                    Vue.set(state.suggestions, String(s.id), {
                      ...s,
                      question: questionId,
                    });
                  });
                  questionBase.suggestions = sIds;
                }

                if (question.tags && Array.isArray(question.tags)) {
                  question.tags.forEach((t: any) => {
                    if (t.id) Vue.set(state.tags, String(t.id), { ...t });
                  });
                }

                Vue.set(state.questions, String(questionId), questionBase);
              });
            }

            Vue.set(state.sections, String(sectionId), {
              ...section,
              questions: questionIds,
            });
          });
        }

        Vue.set(state.steps, String(stepId), {
          ...step,
          sections: sectionIds,
        });
      });
    }

    Vue.set(state.surveys, survey.slug, { ...survey, steps: stepIds });
  },

  // ─── Response Group ───

  STORE_RESPONSE_GROUP(state: FormStoreState, rg: any) {
    if (!rg || !rg.uuid) return;
    const rgBase = { ...rg };

    if (rg.prospect) {
      Vue.set(state.prospects, rg.prospect.uuid, rg.prospect);
      rgBase.prospectUuid = rg.prospect.uuid;
      delete rgBase.prospect;
    }
    delete rgBase.responses;
    Vue.set(state.responseGroups, rg.uuid, rgBase);
  },

  // ─── Responses ───

  LOAD_RESPONSES(
    state: FormStoreState,
    payload: { rgUuid: string; responses: any }
  ) {
    const { rgUuid } = payload;
    let responses = payload.responses;
    if (!state.responses[rgUuid]) {
      Vue.set(state.responses, rgUuid, {});
    }
    // Handle both array format (PHP numeric keys) and object format
    if (Array.isArray(responses)) {
      // Array: each item should have a questionId or question.id
      const obj: any = {};
      responses.forEach((r: any) => {
        const qId = r.questionId || r.question?.id;
        if (qId) obj[String(qId)] = r;
      });
      responses = obj;
    }
    // Les sous-RG viennent du backend en stubs plats {uuid, alias, state, ...}.
    // On les enrichit avec expand/loading/loaded AVANT qu'ils entrent dans le
    // state reactif, sinon Vue 2 ne peut pas tracer ces props (clic sur le
    // toggle sans re-render).
    const normalizeStubs = (stubs: any): any[] => {
      if (!Array.isArray(stubs)) return [];
      return stubs.map((s: any) => {
        if (!s || typeof s !== "object") return s;
        return {
          expand: false,
          loading: false,
          loaded: Array.isArray(s.responses) && s.responses.length > 0,
          ...s,
        };
      });
    };
    Object.keys(responses).forEach((questionId) => {
      const r = responses[questionId];
      if (!r || typeof r !== "object") return;
      // Si une entrée existe déjà, mettre à jour sans remplacer l'objet
      // (pour conserver les références que question.response pointe dessus)
      const existing = state.responses[rgUuid][String(questionId)];
      if (existing) {
        Vue.set(existing, "uuid", r.uuid || "");
        Vue.set(existing, "value", r.value);
        Vue.set(existing, "currentValue", r.value);
        Vue.set(existing, "synced", true);
        Vue.set(existing, "comment", r.comment || "");
        if (r.responseGroups) {
          Vue.set(existing, "responseGroups", normalizeStubs(r.responseGroups));
        }
      } else {
        const entry: any = {
          uuid: r.uuid || "",
          value: r.value,
          currentValue: r.value,
          synced: true,
          questionId: Number(questionId),
          questionUuid: r.questionUuid || "",
          rgUuid,
          responseGroupUuid: rgUuid,
          comment: r.comment || "",
          responseGroups: normalizeStubs(r.responseGroups),
          // Compatibilité avec rg.simplify() qui appelle r.simplify()
          simplify() {
            return {
              uuid: this.uuid,
              value: this.value,
              currentValue: this.currentValue,
              comment: this.comment,
              questionUuid: this.questionUuid,
              responseGroupUuid: this.responseGroupUuid,
              responseGroups: this.responseGroups,
            };
          },
        };
        Vue.set(state.responses[rgUuid], String(questionId), entry);
      }
    });
  },

  // Ajoute/retire un sous-RG sur l'entree response d'une question OBJECT_GROUP
  // de maniere reactive (splice direct fonctionne mal quand question.response
  // n'est pas lui-meme observe par Vue).
  ADD_SUB_RG(
    state: FormStoreState,
    payload: { rgUuid: string; questionId: string | number; subRg: any }
  ) {
    const entry = state.responses[payload.rgUuid]?.[String(payload.questionId)];
    if (!entry) return;
    const stub = {
      expand: false,
      loading: false,
      loaded: Array.isArray(payload.subRg?.responses) && payload.subRg.responses.length > 0,
      ...payload.subRg,
    };
    const current = Array.isArray(entry.responseGroups) ? entry.responseGroups.slice() : [];
    const idx = current.findIndex((r: any) => r && r.uuid === stub.uuid);
    if (idx === -1) current.push(stub);
    else current[idx] = stub;
    Vue.set(entry, "responseGroups", current);
  },

  REMOVE_SUB_RG(
    state: FormStoreState,
    payload: { rgUuid: string; questionId: string | number; subRgUuid: string }
  ) {
    const entry = state.responses[payload.rgUuid]?.[String(payload.questionId)];
    if (!entry || !Array.isArray(entry.responseGroups)) return;
    const next = entry.responseGroups.filter((r: any) => r && r.uuid !== payload.subRgUuid);
    Vue.set(entry, "responseGroups", next);
  },

  SET_RESPONSE_VALUE(
    state: FormStoreState,
    payload: {
      rgUuid: string;
      questionId: string | number;
      value: any;
      synced?: boolean;
    }
  ) {
    const qId = String(payload.questionId);
    if (!state.responses[payload.rgUuid]) {
      Vue.set(state.responses, payload.rgUuid, {});
    }
    const existing = state.responses[payload.rgUuid][qId];
    if (existing) {
      Vue.set(existing, "value", payload.value);
      if (payload.synced !== undefined) {
        Vue.set(existing, "synced", payload.synced);
      }
    } else {
      const entry: any = {
        uuid: "",
        value: payload.value,
        currentValue: null,
        synced: payload.synced !== undefined ? payload.synced : true,
        questionId: Number(qId),
        questionUuid: "",
        rgUuid: payload.rgUuid,
        responseGroupUuid: payload.rgUuid,
        comment: "",
        responseGroups: [],
        simplify() {
          return {
            uuid: this.uuid,
            value: this.value,
            currentValue: this.currentValue,
            comment: this.comment,
            questionUuid: this.questionUuid,
            responseGroupUuid: this.responseGroupUuid,
            responseGroups: this.responseGroups,
          };
        },
      };
      Vue.set(state.responses[payload.rgUuid], qId, entry);
    }
  },

  MARK_SYNCED(
    state: FormStoreState,
    payload: {
      rgUuid: string;
      questionId: string | number;
      uuid: string;
      value: any;
    }
  ) {
    const rgResponses = state.responses[payload.rgUuid];
    if (!rgResponses) return;

    // Chercher par questionId (string) ou par questionUuid en fallback
    let entry = rgResponses[String(payload.questionId)];
    if (!entry) {
      // Fallback: chercher par questionUuid dans toutes les entrées du RG
      const uuid = payload.uuid;
      entry = Object.values(rgResponses).find(
        (e: any) => e.questionUuid === payload.questionId || e.questionId === Number(payload.questionId)
      ) as any;
    }
    if (entry) {
      Vue.set(entry, "synced", true);
      Vue.set(entry, "uuid", payload.uuid);
      Vue.set(entry, "currentValue", payload.value);
    }
  },

  REMOVE_RESPONSE(
    state: FormStoreState,
    payload: { rgUuid: string; questionId: string | number }
  ) {
    const qId = String(payload.questionId);
    if (state.responses[payload.rgUuid]) {
      Vue.delete(state.responses[payload.rgUuid], qId);
    }
  },

  // ─── Sync queue ───

  ADD_PENDING_SYNC(
    state: FormStoreState,
    payload: { rgUuid: string; questionId: string }
  ) {
    const key = `${payload.rgUuid}:${payload.questionId}`;
    Vue.set(state.pendingSync, key, payload);
  },

  CLEAR_PENDING_SYNC(state: FormStoreState) {
    state.pendingSync = {};
  },

  SET_SYNCING(state: FormStoreState, value: boolean) {
    state.isSyncing = value;
  },

  SET_LAST_SYNC_AT(state: FormStoreState, at: string | null) {
    state.lastSyncAt = at;
    persistState(state);
  },

  SET_QUESTIONS_FILTERED(state: FormStoreState, value: any) {
    state.questionsFiltered = value;
  },

  // ─── Reset ───

  RESET(state: FormStoreState) {
    localStorage.removeItem(STORAGE_KEY);
    Object.assign(state, getDefaultState());
  },
};

const actions = {
  // ─── Load a single survey structure (fallback when the delta sync missed it) ───

  async loadSurveyStructure({ state }: any, slug: string): Promise<boolean> {
    if (!slug) return false;
    const res = await api.get(api.form, "api/load/survey/structure/" + slug);
    if (!res || !res.data || res.data.status !== "success" || !res.data.survey) {
      return false;
    }
    const data = res.data;
    // Ingest flat entities directement dans le store (meme shape que api/sync).
    const survey = data.survey;
    Vue.set(state.surveys, survey.slug || String(survey.id), {
      ...survey,
      steps: survey.steps || [],
    });
    const childEntities = [
      "steps",
      "sections",
      "questions",
      "choices",
      "tags",
      "suggestions",
      "conditionGroups",
      "conditions",
      "mappings",
    ];
    childEntities.forEach((key: string) => {
      const bucket = data[key];
      if (!bucket) return;
      const store = (state as any)[key];
      if (!store) return;
      Object.keys(bucket).forEach((id: string) => {
        Vue.set(store, id, bucket[id]);
      });
    });
    return true;
  },

  // ─── Sync form structure via POST /api/sync ───

  async syncFormStructure(
    { commit, state }: any,
    payload?: { branches?: string[] }
  ) {
    const body: any = {
      scope: [
        "surveys",
        "steps",
        "sections",
        "questions",
        "choices",
        "conditionGroups",
        "conditions",
        "mappings",
        "suggestions",
        "tags",
      ],
    };

    if (payload && payload.branches && payload.branches.length > 0) {
      body.branches = payload.branches;
    }

    if (state.lastSyncAt) {
      body.since = state.lastSyncAt;
    }

    const res = await api.post(api.form, "api/sync", body, {
      headers: { Source: "comparateur" },
    });

    if (!res || !res.data || res.data.status !== "success") {
      console.error("Sync failed:", res?.data);
      return { success: false };
    }

    const syncState = res.data.state;

    // Helper pour les suppressions
    const processDeleted = (entity: string, deleted: any[]) => {
      if (!deleted || !Array.isArray(deleted)) return;
      deleted.forEach((id: any) => {
        if (entity === "surveys") {
          Object.keys(state.surveys).forEach((key: string) => {
            if (state.surveys[key] && String(state.surveys[key].id) === String(id)) {
              commit("REMOVE_DATA", { property: "surveys", key });
            }
          });
        } else {
          commit("REMOVE_DATA", { property: entity, key: String(id) });
        }
      });
    };

    // 1. Entités feuilles
    const leafEntities = [
      "choices",
      "conditions",
      "suggestions",
      "mappings",
      "tags",
    ];
    leafEntities.forEach((entity) => {
      if (!syncState[entity]) return;
      if (syncState[entity].updates) {
        Object.keys(syncState[entity].updates).forEach((id: string) => {
          Vue.set(state[entity], id, { ...syncState[entity].updates[id] });
        });
      }
      processDeleted(entity, syncState[entity].deleted);
    });

    // 2. ConditionGroups (avec cross-ref conditions)
    if (syncState.conditionGroups) {
      if (syncState.conditionGroups.updates) {
        Object.keys(syncState.conditionGroups.updates).forEach((id: string) => {
          const cg = { ...syncState.conditionGroups.updates[id] };
          cg.questionUuid = String(cg.question);
          cg.uuid = id;
          Vue.set(state.conditionGroups, id, cg);
          if (cg.conditions && Array.isArray(cg.conditions)) {
            cg.conditions.forEach((cId: any) => {
              const cKey = String(cId);
              if (state.conditions[cKey]) {
                Vue.set(state.conditions[cKey], "conditionGroupUuid", id);
                Vue.set(state.conditions[cKey], "uuid", cKey);
              }
            });
          }
        });
      }
      processDeleted("conditionGroups", syncState.conditionGroups.deleted);
    }

    // 3. Questions
    if (syncState.questions) {
      if (syncState.questions.updates) {
        Object.keys(syncState.questions.updates).forEach((id: string) => {
          const question = { ...syncState.questions.updates[id] };
          if (
            !question.choices ||
            !Array.isArray(question.choices) ||
            question.choices.length === 0
          ) {
            question.choices = [] as number[];
            Object.keys(state.choices).forEach((choiceKey: string) => {
              if (String(state.choices[choiceKey].question) === id) {
                question.choices.push(Number(choiceKey));
              }
            });
          }
          Vue.set(state.questions, id, question);
        });
      }
      processDeleted("questions", syncState.questions.deleted);
    }

    // 4. Sections, Steps, Surveys
    ["sections", "steps"].forEach((entity) => {
      if (!syncState[entity]) return;
      if (syncState[entity].updates) {
        Object.keys(syncState[entity].updates).forEach((id: string) => {
          Vue.set(state[entity], id, { ...syncState[entity].updates[id] });
        });
      }
      processDeleted(entity, syncState[entity].deleted);
    });

    if (syncState.surveys) {
      if (syncState.surveys.updates) {
        Object.keys(syncState.surveys.updates).forEach((id: string) => {
          const survey = { ...syncState.surveys.updates[id] };
          const key = survey.slug || id;
          Vue.set(state.surveys, key, survey);
        });
      }
      processDeleted("surveys", syncState.surveys.deleted);
    }

    commit("SET_LAST_SYNC_AT", res.data.at);
    return { success: true, at: res.data.at };
  },

  // ─── Load RG data ───

  async loadResponseGroupData(
    { commit, state, getters, dispatch }: any,
    rgUuid: string
  ) {
    // 1. Si le RG est deja en store (prechargé par rgBatchLoader ou charge
    // lors d'un precedent mount), on court-circuite sync + fetch. Evite un
    // sync a chaque re-expand d'un sous-RG.
    const cachedRg = state.responseGroups[rgUuid];
    if (cachedRg) {
      const cachedSlug = cachedRg.surveySlug || "";
      if (cachedSlug && !state.surveys[cachedSlug]) {
        await dispatch("loadSurveyStructure", cachedSlug);
      }
      return {
        success: true,
        surveySlug: cachedSlug,
        branchSlug: cachedRg.branchSlug || "",
        rgUuid,
      };
    }

    // 2. Sinon on sync la structure du formulaire et on hydrate les DataTypes.
    await dispatch("syncFormStructure");
    await dispatch("masterStore/loadDataTypes", null, { root: true });

    // 4. Sinon, charger depuis le backend.
    const rgRes = await api.get(api.form, "api/get/rg/" + rgUuid);

    // Fallback sur l'ancien endpoint si le nouveau n'existe pas encore
    let responseGroup: any = null;
    let branchSlug = "";
    let surveySlug = "";

    if (rgRes && rgRes.data && rgRes.data.status === "success" && rgRes.data.responseGroup) {
      responseGroup = rgRes.data.responseGroup;
      surveySlug = rgRes.data.surveySlug || responseGroup.surveySlug || "";
      branchSlug = rgRes.data.branchSlug || responseGroup.branchSlug || "";
    } else {
      // Fallback: ancien endpoint qui renvoie tout (structure + RG)
      const fallbackRes = await api.get(api.form, "api/get/new/data/" + rgUuid);
      if (!fallbackRes || !fallbackRes.data || fallbackRes.data.status !== "success" || !fallbackRes.data.responseGroup) {
        return { success: false, error: "ResponseGroup not found" };
      }
      responseGroup = fallbackRes.data.responseGroup;
      surveySlug = responseGroup.surveySlug || (fallbackRes.data.survey && fallbackRes.data.survey.slug) || "";
      branchSlug = fallbackRes.data.branchSlug || responseGroup.branchSlug || "";
    }

    // 4. Stocker le RG
    commit("STORE_RESPONSE_GROUP", responseGroup);

    // 5. Stocker les réponses indexées par [rgUuid][questionId]
    if (responseGroup.responses) {
      commit("LOAD_RESPONSES", {
        rgUuid: responseGroup.uuid,
        responses: responseGroup.responses,
      });
    }

    // 6. Context
    commit("SET_CURRENT_CONTEXT", {
      rgUuid: responseGroup.uuid,
      surveySlug,
      branchSlug,
    });

    // 7. Si le survey du RG manque en store (ex: sous-survey non rattache a la
    // plateforme au moment du dernier sync delta), on charge sa structure
    // ciblee pour que getSurveyFull puisse reconstruire le formulaire.
    if (surveySlug && !state.surveys[surveySlug]) {
      await dispatch("loadSurveyStructure", surveySlug);
    }

    return {
      success: true,
      surveySlug,
      branchSlug,
      rgUuid: responseGroup.uuid,
    };
  },

  // ─── Response batch save ───

  updateResponse(
    { commit, state, dispatch }: any,
    payload: {
      rgUuid: string;
      questionId: number;
      questionUuid: string;
      value: any;
    }
  ) {
    const qId = String(payload.questionId);

    // 1. Immediate reactive update
    commit("SET_RESPONSE_VALUE", {
      rgUuid: payload.rgUuid,
      questionId: qId,
      value: payload.value,
      synced: false,
    });

    // Set questionUuid if provided
    const entry = state.responses[payload.rgUuid]?.[qId];
    if (entry && payload.questionUuid) {
      Vue.set(entry, "questionUuid", payload.questionUuid);
    }

    // 2. Queue for sync
    commit("ADD_PENDING_SYNC", {
      rgUuid: payload.rgUuid,
      questionId: qId,
    });

    // 3. Debounced flush
    dispatch("scheduleFlush");
  },

  scheduleFlush({ state, dispatch }: any) {
    if (state.syncTimer) {
      clearTimeout(state.syncTimer);
    }
    state.syncTimer = setTimeout(() => {
      dispatch("flushSync");
    }, DEBOUNCE_MS);
  },

  async flushSync({ state, commit }: any) {
    const pending = { ...state.pendingSync };
    const keys = Object.keys(pending);
    if (keys.length === 0) return;

    commit("CLEAR_PENDING_SYNC");
    commit("SET_SYNCING", true);

    const batch = keys
      .map((key) => {
        const { rgUuid, questionId } = pending[key];
        const entry = state.responses[rgUuid]?.[questionId];
        if (!entry) return null;
        return {
          rgUuid,
          questionId: entry.questionId,
          questionUuid: entry.questionUuid,
          value: entry.value,
          uuid: entry.uuid || undefined,
        };
      })
      .filter(Boolean);

    if (batch.length === 0) {
      commit("SET_SYNCING", false);
      return;
    }

    try {
      const res = await api.post(api.form, "api/save/responses", batch);
      if (res && res.data && res.data.status === "success") {
        // Traiter les réponses individuelles du serveur
        if (res.data.responses) {
          res.data.responses.forEach((saved: any) => {
            if (saved.deletions && Array.isArray(saved.deletions)) {
              saved.deletions.forEach((deletedQId: string) => {
                commit("REMOVE_RESPONSE", {
                  rgUuid: saved.rgUuid,
                  questionId: deletedQId,
                });
              });
            }
            if (saved.status === "success" && saved.response) {
              const questionId = saved.response.questionId
                || saved.questionId
                || saved.response.question?.id;
              if (questionId) {
                commit("MARK_SYNCED", {
                  rgUuid: saved.rgUuid,
                  questionId,
                  uuid: saved.response.uuid,
                  value: saved.response.value,
                });
              }
            }
          });
        }
        // Filet de sécurité : marquer toutes les entrées du batch comme synced
        // au cas où le serveur ne renvoie pas le questionId attendu
        batch.forEach((item: any) => {
          const entry = state.responses[item.rgUuid]?.[String(item.questionId)];
          if (entry && !entry.synced) {
            Vue.set(entry, "synced", true);
          }
        });
      } else {
        keys.forEach((key) => {
          commit("ADD_PENDING_SYNC", pending[key]);
        });
        new Popup("Erreur", "Erreur lors de la sauvegarde", "danger", "fad fa-exclamation");
      }
    } catch (e) {
      console.error("flushSync error:", e);
      keys.forEach((key) => {
        commit("ADD_PENDING_SYNC", pending[key]);
      });
    } finally {
      commit("SET_SYNCING", false);
    }
  },

  async forceFlush({ state, dispatch }: any) {
    if (state.syncTimer) {
      clearTimeout(state.syncTimer);
      state.syncTimer = null;
    }
    await dispatch("flushSync");
  },
};

const getters = {
  // ─── Survey reconstruction ───

  getSurveyFull:
    (state: FormStoreState, _getters: any, rootState: any) =>
    (slug: string) => {
      const survey = state.surveys[slug];
      if (!survey) return null;

      const steps = (survey.steps || [])
        .map((stepId: number) => {
          const step = state.steps[String(stepId)];
          if (!step) return null;

          const sections = (step.sections || [])
            .map((sectionId: number) => {
              const section = state.sections[String(sectionId)];
              if (!section) return null;

              const questions = (section.questions || [])
                .map((questionId: number) => {
                  const question = state.questions[String(questionId)];
                  if (!question) return null;

                  // Résoudre les choices : soit depuis question.choices (IDs), soit par lookup inverse
                  let choices: any[] = [];
                  if (question.choices && Array.isArray(question.choices) && question.choices.length > 0) {
                    choices = question.choices
                      .map((cId: number) => state.choices[String(cId)])
                      .filter(Boolean);
                  }
                  if (choices.length === 0) {
                    // Lookup inverse : trouver les choices qui référencent cette question
                    Object.values(state.choices).forEach((c: any) => {
                      if (String(c.question) === String(questionId)) {
                        choices.push(c);
                      }
                    });
                  }

                  // Résoudre les conditionGroups : soit depuis question, soit par lookup inverse
                  let conditionGroups: any[] = [];
                  if (question.conditionGroups && Array.isArray(question.conditionGroups) && question.conditionGroups.length > 0) {
                    conditionGroups = question.conditionGroups
                      .map((cgId: number) => {
                        const cg = state.conditionGroups[String(cgId)];
                        if (!cg) return null;
                        return {
                          ...cg,
                          conditions: (cg.conditions || [])
                            .map((cId: number) => state.conditions[String(cId)])
                            .filter(Boolean),
                        };
                      })
                      .filter(Boolean);
                  }
                  if (conditionGroups.length === 0) {
                    Object.values(state.conditionGroups).forEach((cg: any) => {
                      if (String(cg.question) === String(questionId)) {
                        const resolved = {
                          ...cg,
                          conditions: (cg.conditions || [])
                            .map((cId: number) => state.conditions[String(cId)])
                            .filter(Boolean),
                        };
                        conditionGroups.push(resolved);
                      }
                    });
                  }

                  // Résoudre les suggestions
                  let suggestions: any[] = [];
                  if (question.suggestions && Array.isArray(question.suggestions) && question.suggestions.length > 0) {
                    suggestions = question.suggestions
                      .map((sId: number) => state.suggestions[String(sId)])
                      .filter(Boolean);
                  }
                  if (suggestions.length === 0) {
                    Object.values(state.suggestions).forEach((s: any) => {
                      if (String(s.question) === String(questionId)) {
                        suggestions.push(s);
                      }
                    });
                  }

                  let resolvedType = question.type;
                  if (
                    typeof resolvedType === "number" ||
                    (typeof resolvedType === "string" && !isNaN(Number(resolvedType)))
                  ) {
                    const dataTypes = rootState?.masterStore?.dataTypes || [];
                    const found = dataTypes.find(
                      (dt: any) => dt.id === Number(resolvedType)
                    );
                    resolvedType = found
                      ? { id: found.id, code: found.code, label: found.label, format: found.format, tags: found.tags || [] }
                      : { id: Number(resolvedType), code: "", label: "", format: "", tags: [] };
                  }

                  let tags: any[] = [];
                  if (question.tags && Array.isArray(question.tags)) {
                    tags = question.tags
                      .map((tagId: any) => {
                        if (typeof tagId === "object") return tagId;
                        return state.tags[String(tagId)] || null;
                      })
                      .filter(Boolean);
                  }

                  return {
                    ...question,
                    type: resolvedType,
                    choices,
                    conditionGroups,
                    suggestions,
                    tags,
                  };
                })
                .filter(Boolean);

              return { ...section, questions };
            })
            .filter(Boolean);

          return { ...step, sections };
        })
        .filter(Boolean);

      // Résoudre les mappings
      const mappings = (survey.mappings || [])
        .map((mappingId: number) => state.mappings[String(mappingId)])
        .filter(Boolean);

      return { ...survey, steps, mappings };
    },

  // ─── Response access ───

  getResponse:
    (state: FormStoreState) =>
    (rgUuid: string, questionId: string | number): ResponseEntry | null => {
      return state.responses[rgUuid]?.[String(questionId)] || null;
    },

  getResponseValue:
    (state: FormStoreState) =>
    (rgUuid: string, questionId: string | number): any => {
      return state.responses[rgUuid]?.[String(questionId)]?.value;
    },

  getResponsesForRg:
    (state: FormStoreState) =>
    (rgUuid: string): { [questionId: string]: ResponseEntry } => {
      return state.responses[rgUuid] || {};
    },

  // ─── ResponseGroup ───

  getResponseGroup:
    (state: FormStoreState) =>
    (uuid: string) => {
      const rg = state.responseGroups[uuid];
      if (!rg) return null;
      return {
        ...rg,
        prospect: rg.prospectUuid ? state.prospects[rg.prospectUuid] : null,
      };
    },

  // ─── Sync state ───

  hasPendingChanges: (state: FormStoreState): boolean => {
    return Object.keys(state.pendingSync).length > 0;
  },

  // ─── Context ───

  currentContext: (state: FormStoreState) => ({
    responseGroup: state.currentResponseGroupUuid
      ? state.responseGroups[state.currentResponseGroupUuid]
      : null,
    survey: state.currentSurveySlug
      ? state.surveys[state.currentSurveySlug]
      : null,
    branchSlug: state.currentBranchSlug,
  }),

  // ─── Question helpers ───

  getQuestion:
    (state: FormStoreState) =>
    (id: string | number) => {
      return state.questions[String(id)] || null;
    },

  getChoicesForQuestion:
    (state: FormStoreState) =>
    (questionId: string | number) => {
      const question = state.questions[String(questionId)];
      if (!question || !question.choices) return [];
      return question.choices
        .map((cId: number) => state.choices[String(cId)])
        .filter(Boolean);
    },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
